import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { catchError, debounceTime, distinctUntilChanged, switchMap, tap } from 'rxjs/operators';
import { Observable, of, Subscription } from 'rxjs';

import { CountryModel, StateModel } from '@data-layer/models/common';
import { CodeRepository } from '@data-layer/repositories';
import { IErrorForm } from '@shared/form-wrapper/interfaces';

@Component({
  selector: 'state-dropdown',
  templateUrl: './state-dropdown.component.html',
  styleUrls: ['./state-dropdown.component.scss'],
})
export class StateDropdownComponent implements OnInit, OnDestroy {
  @Input() public countryFc: UntypedFormControl;
  @Input() public stateFc: UntypedFormControl;
  @Input() public errors: IErrorForm;
  @Input() public isShowRequired: boolean = false;
  @Input() public isClearable: boolean = true;
  @Input() public countryCode: string = 'US';
  @Input() public label: string = 'FORM_LABEL.STATE';

  public selectedState: any = null;
  public isCountryNotExist: boolean = false;
  public isInitPassed: boolean = false;

  public states: StateModel[] = [];
  public statesTypeahead: EventEmitter<string> = new EventEmitter<string>();

  public statesLoading: boolean = false;

  private subscription: Subscription[] = [];

  constructor(private codeRepository: CodeRepository, private cdr: ChangeDetectorRef) {}

  public ngOnInit(): void {
    this.setControls();
    this.subscription.push(
      this.countryFc.valueChanges.subscribe((country: CountryModel) => this.upgradeStateControl(country)),
      this.stateFc.valueChanges.subscribe((data) => {
        this.selectedState = data || null;
      }),
    );
    this.onChangeState(this.stateFc.value);

    this.upgradeStateControl(this.countryFc.value);
    this.isInitPassed = true;
    this.loadStates();
  }

  public ngOnDestroy(): void {
    this.subscription.forEach((sub) => sub.unsubscribe());
  }

  public onChangeState(state: StateModel) {
    if (!state) {
      this.stateFc.reset();
      return;
    }

    this.stateFc.patchValue(state);
    if (this.isInitPassed) {
      this.stateFc.markAsDirty();
      this.stateFc.markAsTouched();
    }
  }

  public onClear(): void {
    this.stateFc.reset();
    this.cdr.markForCheck();
  }

  // The dropdown's disabled state comes from stateFc itself via the reactive [formControl] directive —
  // no separate [disabled] binding (it triggered the Angular "disabled attribute with a reactive form
  // directive" warning and lagged behind the control's actual state).
  private upgradeStateControl(country: CountryModel) {
    this.isCountryNotExist = !country || !country.code;

    if (this.isCountryNotExist) {
      this.stateFc.reset();
      this.states = [];
      this.selectedState = null;
    }
  }

  private setControls(): void {
    if (!this.countryFc || !this.stateFc) {
      throw new Error(`You can't use this component!`);
    }
  }

  private getStates(searchStr: string): Observable<StateModel[]> {
    const countryCode = this.countryFc?.value?.code;

    return this.codeRepository.searchState(countryCode, searchStr);
  }

  private loadStates() {
    this.subscription.push(
      this.statesTypeahead
        .pipe(
          distinctUntilChanged(),
          debounceTime(300),
          tap(() => (this.statesLoading = true)),
          switchMap((term) =>
            !term
              ? this.emptyCountry()
              : this.getStates(term).pipe(
                  catchError(() => this.emptyCountry()),
                  tap(() => (this.statesLoading = false)),
                ),
          ),
          catchError(() => this.emptyCountry()),
        )
        .subscribe((statesRes: StateModel[]) => {
          this.states = [...statesRes];
          this.cdr.markForCheck();
        }),
    );
  }

  private emptyCountry(): Observable<StateModel[]> {
    this.statesLoading = false;
    return of([]);
  }
}
